/** Per-set load logging + history-relative intensity. */

export interface WorkoutSet {
  reps: number
  /** Absolute load (lb or kg - user-consistent). */
  weight: number
}

/** Total volume: Σ(weight × reps) across sets. */
export function sessionLoad(sets: WorkoutSet[]): number {
  return sets.reduce((sum, s) => {
    const reps = Math.max(0, s.reps)
    const weight = Math.max(0, s.weight)
    return sum + weight * reps
  }, 0)
}

export function averageWeight(sets: WorkoutSet[]): number {
  if (!sets.length) return 0
  return sets.reduce((s, x) => s + Math.max(0, x.weight), 0) / sets.length
}

export function totalReps(sets: WorkoutSet[]): number {
  return sets.reduce((s, x) => s + Math.max(0, x.reps), 0)
}

/**
 * Intensity 1-10 from this session vs prior sessions for the same exercise.
 * - No prior history → 10 (first run establishes the baseline)
 * - Otherwise → 10 × (currentLoad / bestPriorLoad), clamped to [1, 10]
 */
export function intensityFromHistory(
  currentSets: WorkoutSet[],
  priorSessionSets: WorkoutSet[][],
): number {
  const current = sessionLoad(currentSets)
  if (!priorSessionSets.length) return 10

  const bestPrior = Math.max(0, ...priorSessionSets.map(sessionLoad))
  if (bestPrior <= 0) {
    // Prior sessions logged with zero load - treat as first real baseline
    return current > 0 ? 10 : 1
  }
  if (current <= 0) return 1

  const raw = 10 * (current / bestPrior)
  return Math.min(10, Math.max(1, Math.round(raw * 10) / 10))
}

/** Peak fatigue 0-1 from set volume + relative intensity. */
export function computePeakFromSets(sets: WorkoutSet[], intensity: number): number {
  const n = Math.max(1, sets.length)
  const avgReps = totalReps(sets) / n
  const volumeFactor = Math.min(1.5, Math.max(0.4, avgReps / 10))
  const i = Math.min(10, Math.max(1, intensity))
  const raw = 0.2 + 0.15 * n * (i / 10) * volumeFactor
  return Math.min(1, Math.max(0.2, raw))
}

export function formatSetsSummary(sets: WorkoutSet[]): string {
  if (!sets.length) return '0 sets'
  const parts = sets.map((s) => `${s.weight}×${s.reps}`)
  return `${sets.length} set${sets.length === 1 ? '' : 's'} · ${parts.join(', ')}`
}

/** Suggest starting sets from the latest matching workout, or a 3×12 @ suggestedWeight starter. */
export function suggestSets(
  priorSets: WorkoutSet[] | undefined,
  opts?: { setCount?: number; defaultReps?: number; defaultWeight?: number },
): WorkoutSet[] {
  const setCount = opts?.setCount ?? 3
  const defaultReps = opts?.defaultReps ?? 12
  const defaultWeight = opts?.defaultWeight ?? 10

  if (priorSets?.length) {
    return priorSets.map((s) => ({
      reps: Math.max(1, s.reps),
      weight: Math.max(0, s.weight),
    }))
  }

  return Array.from({ length: setCount }, () => ({
    reps: defaultReps,
    weight: defaultWeight,
  }))
}
